<template>
  <div>
    <!-- Alert: No item found -->
    <LivnessCheckModal />
    <SendNotificationModal :driver="driverData" />
    <SendAlertModal :driver="driverData" />
    <LearningCenterBlockModal :driver="driverData" />
    <b-alert
      variant="danger"
      :show="driverData === undefined"
    >
      <h4 class="alert-heading">
        Error fetching driver data
      </h4>
      <div class="alert-body">
        No Driver found with this driver id. Check
        <b-link
          class="alert-link"
          :to="{ name: 'drivers-list'}"
        >
          Drivers List
        </b-link>
        for other drivers.
      </div>
    </b-alert>

    <b-tabs
      v-model="tabIndex"
    >
      <b-tab>
        <template #title>
          <feather-icon
            icon="UserIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Details</span>
        </template>
        <driver-details-tab
          :loading-driver-data="loadingDriverData"
          :driver-data="driverData"
          :driver-rating="rating"
          @refetch-driver-details="fetchDriverInfo"
        />
      </b-tab>
      <b-tab
        v-if="$can('read', 'driver-inactivations')"
      >
        <template #title>
          <feather-icon
            icon="ListIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Inactivations</span>
        </template>
        <inactivation-list-tab
          :inactivation-list-loading="loadingInactivationList"
          @refetch-inactivation-list="fetchDriverInactivations"
          @refetch-driver-details="fetchDriverInfo"
        />
      </b-tab>
      <b-tab
        v-if="$can('read', 'driver-wallet-settings') || $can('read', 'driver-wallet-transactions')"
        :active="$route.query['active-tab'] === 'wallet'"
      >
        <template #title>
          <feather-icon
            icon="BriefcaseIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Wallet</span>
        </template>
        <driver-wallet-tab
          :loading-driver-wallet="loadingDriverWallet"
          :driver-wallet-data="driverWallet"
          @refetch-driver-wallet="fetchDriverWallet"
          @reversed="fetchDriverWallet"
          @refetch-driver-inactivations="fetchDriverInactivations"
        />
      </b-tab>
      <b-tab
        v-if="$can('read', 'driver-notes')"
        :disabled="!driverData"
      >
        <template #title>
          <feather-icon
            icon="EditIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Notes</span>
        </template>
        <sticky-driver-data />
        <notes-list notable-type="driver" />
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon
            icon="TruckIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Trips History</span>
        </template>
        <sticky-driver-data />
        <CanceledOnDispatchDriverTrips />
        <driver-user-trips
          type="driver"
        />
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon
            icon="BarChart2Icon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Statistics</span>
        </template>
        <sticky-driver-data />
        <statistics-tab
          v-if="driverData"
          :driver-data="driverData"
        />
      </b-tab>
      <b-tab :disabled="!reviews">
        <template #title>
          <feather-icon
            icon="ActivityIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Reviews</span>
        </template>
        <reviews-tab
          :reviews="reviews"
          :driver-rating="rating"
          @refetch-reviews="fetchReviews"
        />
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon
            icon="PhoneIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span>Support</span>
        </template>
        <support-tab
          user-type="driver"
          :user-external-id="driverExternalId"
        />
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon
            icon="CreditCardIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span>Licenses</span>
        </template>
        <DriverLicenseTab />
      </b-tab>
      <!-- <b-tab :disabled="!driverWallet">
        <template #title>
          <feather-icon
            icon="GiftIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span>Tiers</span>
        </template>
        <TiersTab
          v-if="driverWallet"
          :wallet-id="driverWallet.id"
        />
      </b-tab> -->
      <b-tab :active="$route.query['active-tab'] === 'violations'">
        <template #title>
          <feather-icon
            icon="XIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span>Violations</span>
        </template>
        <ViolationsTab />
      </b-tab>
      <b-tab v-if="driverWallet">
        <template #title>
          <feather-icon
            icon="DollarSignIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span>Payouts</span>
        </template>
        <DriverPayoutsTab :wallet="driverWallet" />
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon
            icon="ClockIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Shifts</span>
        </template>
        <DriverShiftsTab
          :driver-id="driverId"
          :loading-driver-data="loadingDriverData"
        />
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon
            icon="EditIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Reset Acceptance Rate</span>
        </template>
        <ResetAcceptanceTab />
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon
            icon="HelpCircleIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Quiz Progress</span>
        </template>
        <QuizProgressTab
          v-if="driverData"
          :driver-data="driverData"
        />
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon
            icon="MapPinIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Driver Destinations</span>
        </template>
        <DriverDestinationsTab
          v-if="driverId"
          :driver-id="driverId"
        />
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import {
  BAlert, BLink, BTabs, BTab,
} from 'bootstrap-vue'

import {
  provide, ref,
} from '@vue/composition-api'
import router from '@/router'
import driverViewStoreModule from './driverViewStoreModule'
import driverStoreModule from '../driverStoreModule'
import transactionTypesStore from '../../billing/transaction-types/transactionTypesStore'
import useDriverView from './useDriverView'

import SupportTab from '@/views/driver-user-shared/support/SupportTab.vue'
import ReviewsTab from './tabs/reviews-tab/ReviewsTab.vue'
import DriverDetailsTab from './tabs/driver-details/DriverDetailsTab.vue'
import InactivationListTab from './tabs/inactivation-list/InactivationListTab.vue'
import DriverWalletTab from './tabs/driver-wallet/DriverWalletTab.vue'
import NotesList from '@/views/driver-user-shared/notes/driver-user-notes-tab/NotesList.vue'
import DriverUserTrips from '@/views/driver-user-shared/driver-user-trips/components/DriverUserTrips.vue'
import StatisticsTab from './tabs/statistics-tab/StatisticsTab.vue'
import useRegisterStoreModule from '@/hooks/useRegisterStoreModule'
import notificationsStore from '@/views/marketing/notifications/store'
import reviewsStore from '@/views/driver-user-shared/driver-user-reviews/store'
import StickyDriverData from './StickyDriverData.vue'
import tripsStore from '@/views/trips/store'
import SendNotificationModal from './tabs/SendNotificationModal.vue'
import CanceledOnDispatchDriverTrips from '../components/CanceledOnDispatchDriverTrips.vue'
import notesStore from '@/views/driver-user-shared/notes/store'
import DriverLicenseTab from './tabs/driver-license/DriverLicenseTab.vue'
import tiersStore from '@/views/marketing/tiers/store'
import ViolationsTab from './tabs/violations/ViolationsTab.vue'
import ResetAcceptanceTab from './tabs/driver-details/ResetAcceptanceTab.vue'
import SendAlertModal from './tabs/SendAlertModal.vue'
import DriverPayoutsTab from './tabs/payouts/DriverPayoutsTab.vue'
import DriverShiftsTab from './tabs/driver-shifts/DriverShiftsTab.vue'
import LivnessCheckModal from './tabs/LivnessCheckModal.vue'
import QuizProgressTab from './tabs/quiz-progress/QuizProgressTab.vue'
import LearningCenterBlockModal from './tabs/LearningCenterBlockModal.vue'
import DriverDestinationsTab from './tabs/driver-destinations/DriverDestinationsTab.vue'

export default {
  components: {
    BAlert,
    BLink,
    SendAlertModal,
    BTabs,
    LivnessCheckModal,
    LearningCenterBlockModal,
    DriverPayoutsTab,
    DriverShiftsTab,
    ViolationsTab,
    BTab,
    DriverDetailsTab,
    SupportTab,
    ResetAcceptanceTab,
    SendNotificationModal,
    InactivationListTab,
    CanceledOnDispatchDriverTrips,
    DriverWalletTab,
    DriverLicenseTab,
    NotesList,
    DriverUserTrips,
    StatisticsTab,
    ReviewsTab,
    StickyDriverData,
    QuizProgressTab,
    DriverDestinationsTab,
  },
  setup(props, ctx) {
    const cardViewMode = ref(true)
    provide('cardViewMode', cardViewMode)

    useRegisterStoreModule('reviews', reviewsStore)
    useRegisterStoreModule('driverView', driverViewStoreModule)
    useRegisterStoreModule('transactionTypes', transactionTypesStore)
    useRegisterStoreModule('drivers', driverStoreModule)
    useRegisterStoreModule('trips', tripsStore)
    useRegisterStoreModule('notifications', notificationsStore)
    useRegisterStoreModule('notes', notesStore)
    useRegisterStoreModule('tiers', tiersStore)

    return {
      ...useDriverView(props, ctx),
    }
  },
}
</script>

<style scoped>

</style>
